import useSWR, { type SWRConfiguration } from 'swr';

/**
 * A hook to fetch data from public api's in DC BE.
 * this prevents multiple calls when de state is still loading
 * @param fetcher The DC BE client API fetch method
 * @param options The options for the API endpoint
 * @param keys Keys on which the data needs to be fetched
 * @returns useSWR response object
 *
 * @example
 * import { getApiLabelsByLabelPricingDynamic } from "@eneco/dc-be/client";
 * const {data, isLoading, error} = usePublicDCBE(getApiLabelsByLabelPricingDynamic, {
 *   query: {
 *     startDate: '2024-08-01',
 *     endDate: '2024-08-01',
 *     aggregation: 'hourly',
 *   }
 * })
 */

type FetchOptions = {
  path?: {
    [key: string]: string;
  };
  query?: {
    [key: string]: unknown;
  };
};

export default function usePublicDCBE<T extends FetchOptions, W extends { response: { status: number } }>(
  fetcher: (options: T) => Promise<W>,
  options: T,
  keys: string[] | null = [],
  shouldFetch: boolean = true,
  signal?: AbortSignal,
  swrConfig?: SWRConfiguration<W>,
) {
  let hasPath = true;
  if (options.path) {
    Object.values(options.path).forEach(value => {
      if (!value) {
        hasPath = false;
      }
    });
  }
  const computedShouldFetch = keys && keys.length > 0 && hasPath && shouldFetch;
  const swrKey = computedShouldFetch ? keys.join() : null;

  const fetcherOptions = signal ? { ...options, signal } : options;

  const {
    data,
    error: swrError,
    isLoading,
    isValidating,
  } = useSWR<W>(
    swrKey,
    () => (computedShouldFetch ? fetcher(fetcherOptions as T) : Promise.reject('error')),
    swrConfig,
  );
  let error = swrError;

  const response = data?.response;
  if (response?.status && response.status > 400) {
    error = 'Something went wrong';
  }

  return { data, error, isLoading, isValidating };
}
